import {Alert, Autocomplete, Box, Chip, Stack, Switch} from "@mui/material";
import * as React from "react";
import Typography from "@mui/material/Typography";
import {useTranslation} from "react-i18next";
import Button from "@mui/material/Button";
import AddIcon from '@mui/icons-material/Add';
import StarIcon from '@mui/icons-material/Star';
import {
    GenderType,
    Profile,
    ProfileStatusCode
} from "crqlar-react-common/dist/models/profile/profile.model";
import Property from "crqlar-react-common/dist/components/Property";
import Form from "@rjsf/mui";
import validator from "@rjsf/validator-ajv8";
import {editableDataTags, mergeDataTags} from "../../common/dataTags.util";
import TextField from "@mui/material/TextField";
import {
    getNameForLanguage,
    ISO_LANGS
} from "crqlar-react-common/dist/utils/translations.util";
import {
    getCountryISO2Cdes,
    getCountryNameForISO2
} from "crqlar-react-common/dist/utils/country.utils";
import AdditionalProperties
    from "crqlar-react-common/dist/components/AdditionalProperties";
import Emails from "./Emails";
import AddressField from "crqlar-react-common/dist/components/AddressField";
import InternalNotes from "./InternalNotes";
import ExtraFields from "./ExtraFields";
import PreferencesField from "./Preferences";
import MembershipField from "./Membership";
import {
    convertLocalDateToDate,
    formatDateLocalISO
} from "crqlar-react-common/dist/utils/date.util";
import CDatePicker from "crqlar-react-common/dist/components/CDatePicker";
import ExternalInfoPreview from "../../common/ExternalInfoPreview";
import {UseEntityData} from "crqlar-react-common/dist/components/useEntity";
import {
    ProfileType
} from "crqlar-react-common/dist/models/profile/profileType.model";
import PhoneField from "./PhoneField";
import Documents from "./Documents";
import Urls from "./Urls";
import ContactPersons from "./ContactPersons";
import CompanyField from "./CompanyField";
import CopyAdornment from "crqlar-react-common/dist/components/CopyAdornment";
import {onlyCrqlarV2External} from "crqlar-react-common/dist/utils/profile.util";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {useAllSectors} from "crqlar-react-common/dist/utils/hooks/sectors/useSectors";
import AcademySection from "./AcademySection";
import {ACADEMY_PROFILE_TAG_KEYS} from "./academyProfile.util";
import {academySector} from "../../common/academyTags.util";

interface Props {
    profileData: UseEntityData<Profile>
}

const ProfileDetailsForm = ({profileData}: Props) => {

    //region State

    const {t} = useTranslation()
    // Read-only users must not see guest contact data (emails, phones, addresses).
    const readOnly = useReadOnly()
    const profile = profileData.item
    const setProfile = profileData.setItem

    const managedExternally = !!profile.primaryProvider && !onlyCrqlarV2External(profile)
    const managedFieldPlaceholder = managedExternally ? t('(Not set)') : undefined
    const isCompany =
        profile.profileTypeC === ProfileType.GDS ||
        profile.profileTypeC === ProfileType.CORPORATION ||
        profile.profileTypeC === ProfileType.TRAVEL_AGENT ||
        profile.profileTypeC === ProfileType.WHOLESALER ||
        profile.profileTypeC === ProfileType.GROUP ||
        profile.profileTypeC === ProfileType.TOUR_OPERATOR ||
        profile.profileTypeC === ProfileType.CRO ||
        profile.profileTypeC === ProfileType.REP_COMPANY ||
        profile.profileTypeC === ProfileType.INTERNET_BROKER ||
        profile.profileTypeC === ProfileType.AIRLINE ||
        profile.profileTypeC === ProfileType.HOTEL ||
        profile.profileTypeC === ProfileType.CAR_RENTAL ||
        profile.profileTypeC === ProfileType.CRUISE_LINE ||
        profile.profileTypeC === ProfileType.EVENT_HOST ||
        profile.profileTypeC === ProfileType.SUPPLIER_PARTNER ||
        profile.profileTypeC === ProfileType.BILLING_CONTACT ||
        profile.profileTypeC === ProfileType.AUTHORIZED_SIGNATURE ||
        profile.profileTypeC === ProfileType.GENERAL_SERVICE_CONTRACTOR ||
        profile.profileTypeC === ProfileType.ARRANGER ||
        profile.profileTypeC === ProfileType.ASSOCIATION ||
        profile.profileTypeC === ProfileType.TRAVEL_AGENCY

    const isRegularProfile = !isCompany

    // Only an organisation with an Akademie sector gets the Akademie section, which today is
    // Forsthofgut alone. While it shows, it owns its tags and the raw editor below leaves them out.
    const akademie = academySector(useAllSectors())
    const reservedTags = akademie ? ACADEMY_PROFILE_TAG_KEYS : []

    //endregion Sate

    //region Handlers

    const updateField = (field: keyof Profile, value: any) => {
        setProfile({...profile, [field]: value})
    }

    //endregion Handlers

    //region UI

    const sectionTitleSx = {
        fontSize: {xs: '18px', sm: '20px'},
        lineHeight: 1.25,
    }

    const fieldGroupGap = {xs: 1.5, sm: 1}
    const repeatableItemGap = {xs: 3, sm: 2.5}
    const primaryTitle = (title: string) => (
        <Stack
            component={'span'}
            direction={'row'}
            gap={0.5}
            alignItems={'center'}
            sx={{display: 'inline-flex'}}
        >
            <StarIcon sx={{fontSize: '1em', lineHeight: 1, flexShrink: 0}}/>
            <span>{title}</span>
        </Stack>
    )

    const managedExternallySection = (
        managedExternally &&
        <Alert severity={'warning'} sx={{mb: 3}}>
            {t('This profile is managed externally and cannot be edited here', {source: profile.primaryProvider})}
        </Alert>
    )

    const basicInfoSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <Typography variant={'h5'} textAlign={'start'} sx={sectionTitleSx}>
                {t('Basic info')}
            </Typography>

            <Stack gap={fieldGroupGap}>

            <Property
                title={t('Id')}
            >
                <TextField
                    id={'id'}
                    size={'small'}
                    value={profile.id}
                    fullWidth
                    required
                    disabled={true}
                    InputProps={{
                        endAdornment: (
                            <CopyAdornment
                                value={
                                    profile.id || '-'
                                }
                            />
                        ),
                    }}
                />
            </Property>

            <Property
                title={t('Profile type')}
            >
                <Autocomplete
                    id={'profileType'}
                    options={Object.values(ProfileType)}
                    getOptionLabel={(option: any) => option ? t(`Profile type ${option}`) : ''}
                    value={profile.profileTypeC ?? null}
                    fullWidth
                    onChange={(e, value) => {
                        updateField('profileTypeC', value ?? undefined)
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            fullWidth
                            size={'small'}
                            placeholder={managedFieldPlaceholder}
                        />
                    )}
                    disabled={managedExternally}
                />
            </Property>

            <Property
                title={t('Status')}
            >
                <Autocomplete
                    id={'status'}
                    options={Object.values(ProfileStatusCode)}
                    getOptionLabel={(option: any) => option ? t(`Profile status ${option}`) : ''}
                    value={profile.statusCodeC ?? null}
                    fullWidth
                    onChange={(e, value) => {
                        updateField('statusCodeC', value ?? undefined)
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            fullWidth
                            size={'small'}
                            placeholder={managedFieldPlaceholder}
                        />
                    )}
                    disabled={managedExternally}
                />
            </Property>

            {
                isRegularProfile &&
                <>
                    <Property
                        title={t('Name prefix')}
                    >
                        <TextField
                            id={'name-prefix'}
                            size={'small'}
                            value={profile.namePrefix}
                            onChange={(e) => updateField('namePrefix', e.target.value)}
                            fullWidth
                            required
                            placeholder={managedFieldPlaceholder}
                            disabled={managedExternally}
                        />
                    </Property>

                    <Property
                        title={t('First Name')}
                    >
                        <TextField
                            id={'name'}
                            size={'small'}
                            value={profile.firstName}
                            onChange={(e) => updateField('firstName', e.target.value)}
                            fullWidth
                            required
                            placeholder={managedFieldPlaceholder}
                            disabled={managedExternally}
                        />
                    </Property>
                </>
            }

            <Property
                title={isCompany ? t('Name') : t('Last Name')}
            >
                <TextField
                    id={'last-name'}
                    size={'small'}
                    value={profile.lastName}
                    onChange={(e) => updateField('lastName', e.target.value)}
                    fullWidth
                    required
                    placeholder={managedFieldPlaceholder}
                    disabled={managedExternally}
                />
            </Property>

            {
                isCompany &&
                <>
                    <Property
                        title={t('Short Name')}
                    >
                        <TextField
                            id={'short-name'}
                            size={'small'}
                            value={profile.shortname}
                            onChange={(e) => updateField('shortname', e.target.value)}
                            fullWidth
                            required
                            placeholder={managedFieldPlaceholder}
                            disabled={managedExternally}
                        />
                    </Property>

                </>
            }

            {/* Department sat inside the company-only block above, so an employee profile never
                showed it, which is the one profile type that always has a department. Required
                stays company-only: there it was already, and marking it required on an employee
                would promise an enforcement this form does not do. */}
            {(isCompany || profile.profileTypeC === ProfileType.EMPLOYEE) && (
                <Property
                    title={t('Department')}
                >
                    <TextField
                        id={'department'}
                        size={'small'}
                        value={profile.department || ''}
                        onChange={(e) => updateField('department', e.target.value)}
                        fullWidth
                        required={isCompany}
                        placeholder={managedFieldPlaceholder}
                        disabled={managedExternally}
                    />
                </Property>
            )}

            {
                isRegularProfile &&
                <>
                    <Property
                        title={t('Gender')}
                    >
                        <Autocomplete
                            id={'gender'}
                            options={Object.keys(GenderType)}
                            getOptionLabel={(option: any) => option ? t(`Gender type ${option}`) : ''}
                            value={profile.gender ?? null}
                            fullWidth
                            onChange={(e, value) => {
                                updateField('gender', value ?? undefined)
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    fullWidth
                                    size={'small'}
                                    placeholder={managedFieldPlaceholder}
                                />
                            )}
                            disabled={managedExternally}
                        />
                    </Property>
                </>
            }

            <Property
                title={t('Primary language')}
            >
                <Autocomplete
                    id={'language-code'}
                    options={ISO_LANGS.map(lang => lang.code)}
                    getOptionLabel={(option: any) => option ? getNameForLanguage(option) || option : ''}
                    value={profile.languageCode ?? null}
                    fullWidth
                    onChange={(e, value) => {
                        updateField('languageCode', value ?? undefined)
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            fullWidth
                            size={'small'}
                            placeholder={managedFieldPlaceholder}
                        />
                    )}
                    disabled={managedExternally}
                />
            </Property>

            <Property
                title={t('Nationality')}
            >
                <Autocomplete
                    id={'nationality'}
                    options={getCountryISO2Cdes()}
                    getOptionLabel={(option: any) => option ? getCountryNameForISO2(option) || option : ''}
                    value={profile.nationality ?? null}
                    fullWidth
                    onChange={(e, value) => {
                        updateField('nationality', value ?? undefined)
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            fullWidth
                            size={'small'}
                            placeholder={managedFieldPlaceholder}
                        />
                    )}
                    disabled={managedExternally}
                />
            </Property>

            {
                isRegularProfile &&
                <>
                    <Property
                        title={t('Birthday')}
                    >
                        <CDatePicker
                            id={'birthday'}
                            value={convertLocalDateToDate(profile.birthDay)}
                            dateOnly
                            onChange={e => {
                                updateField('birthDay', formatDateLocalISO(e))
                            }}
                            placeholder={managedFieldPlaceholder}
                            disabled={managedExternally}
                        />
                    </Property>
                </>
            }

            <Property
                title={t('VIP Code')}
            >
                <TextField
                    id={'vip-code'}
                    size={'small'}
                    value={profile.vipCode}
                    onChange={(e) => updateField('vipCode', e.target.value)}
                    fullWidth
                    required
                    placeholder={managedFieldPlaceholder}
                    disabled={managedExternally}
                />
            </Property>

            </Stack>
        </Stack>
    )

    const basicInfoSectionExtended = (
        <AdditionalProperties title={t('Basic info extended')} variant={'outlined'}>
            <Stack gap={fieldGroupGap}>

            <Property
                title={t('Name Suffix')}
            >
                <TextField
                    id={'name-suffix'}
                    size={'small'}
                    value={profile.nameSuffix}
                    onChange={(e) => updateField('nameSuffix', e.target.value)}
                    fullWidth
                    required
                    placeholder={managedFieldPlaceholder}
                    disabled={managedExternally}
                />
            </Property>

            <Property
                title={t('Middle Name')}
            >
                <TextField
                    id={'middle-name'}
                    size={'small'}
                    value={profile.middleName}
                    onChange={(e) => updateField('middleName', e.target.value)}
                    fullWidth
                    required
                    placeholder={managedFieldPlaceholder}
                    disabled={managedExternally}
                />
            </Property>

            <Property
                title={t('Last Name Prefix')}
            >
                <TextField
                    id={'last-name-prefix'}
                    size={'small'}
                    value={profile.lastNamePrefix}
                    onChange={(e) => updateField('lastNamePrefix', e.target.value)}
                    fullWidth
                    required
                    placeholder={managedFieldPlaceholder}
                    disabled={managedExternally}
                />
            </Property>

            <Property
                title={t('Academic Title')}
            >
                <TextField
                    id={'academic-title'}
                    size={'small'}
                    value={profile.academicTitle}
                    onChange={(e) => updateField('academicTitle', e.target.value)}
                    fullWidth
                    required
                    placeholder={managedFieldPlaceholder}
                    disabled={managedExternally}
                />
            </Property>

            <Property
                title={t('Business Title')}
            >
                <TextField
                    id={'business-title'}
                    size={'small'}
                    value={profile.businessTitle}
                    onChange={(e) => updateField('businessTitle', e.target.value)}
                    fullWidth
                    required
                    placeholder={managedFieldPlaceholder}
                    disabled={managedExternally}
                />
            </Property>

            <Property
                title={t('Familiar name')}
            >
                <TextField
                    id={'familiar-name'}
                    size={'small'}
                    value={profile.familiarName}
                    onChange={(e) => updateField('familiarName', e.target.value)}
                    fullWidth
                    required
                    placeholder={managedFieldPlaceholder}
                    disabled={managedExternally}
                />
            </Property>

            <Property
                title={t('Do not share gender')}
            >
                <Switch
                    id={'gender-no-share'}
                    checked={profile.genderNoShare}
                    onChange={(e) => updateField('genderNoShare', e.target.checked)}
                    disabled={managedExternally}
                />
            </Property>

            {/*<Property*/}
            {/*    title={t('Birthday')}*/}
            {/*>*/}
            {/*    */}
            {/*</Property>*/}

            <Property
                title={t('Additional languages')}
            >
                <Autocomplete
                    id={'additional-languages'}
                    multiple
                    options={ISO_LANGS.map(lang => lang.code.toLowerCase())}
                    getOptionLabel={(option: any) => option ? getNameForLanguage(option) || option : ''}
                    value={profile.additionalLanguages || []}
                    fullWidth
                    onChange={(e, value) => {
                        updateField('additionalLanguages', value)
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            fullWidth
                            size={'small'}
                            placeholder={managedFieldPlaceholder}
                        />
                    )}
                    disabled={managedExternally}
                />
            </Property>


            </Stack>
        </AdditionalProperties>
    )

    const contactInfoSection = (
        <AdditionalProperties title={t('Contact info')} variant={'outlined'}>
            <Stack gap={fieldGroupGap}>
            <Property
                title={t('Receive regular info emails')}
            >
                <Switch
                    id={'receive-emails'}
                    checked={profile.receiveEmails}
                    onChange={(e) => updateField('receiveEmails', e.target.checked)}
                    disabled={managedExternally}
                />
            </Property>

            <Property
                title={t('Receive promotional emails & newsletter')}
            >
                <Switch
                    id={'receive-newsletters'}
                    checked={profile.receiveNewsletters}
                    onChange={(e) => updateField('receiveNewsletters', e.target.checked)}
                    disabled={managedExternally}
                />
            </Property>

            <Property
                title={t('Receive promotional SMS')}
            >
                <Switch
                    id={'receive-sms'}
                    checked={profile.receiveSMS}
                    onChange={(e) => updateField('receiveSMS', e.target.checked)}
                    disabled={managedExternally}
                />
            </Property>

            <Property
                title={t('Receive promotional calls')}
            >
                <Switch
                    id={'receive-calls'}
                    checked={profile.receivePromoCalls}
                    onChange={(e) => updateField('receivePromoCalls', e.target.checked)}
                    disabled={managedExternally}
                />
            </Property>

            <Property
                title={t('Receive promotional physical letters')}
            >
                <Switch
                    id={'receive-letters'}
                    checked={profile.receiveLetters}
                    onChange={(e) => updateField('receiveLetters', e.target.checked)}
                    disabled={managedExternally}
                />
            </Property>

            <Property
                title={t('Receive stay related emails')}
            >
                <Switch
                    id={'stay-receive-emails'}
                    checked={profile.stayReceiveEmails}
                    onChange={(e) => updateField('stayReceiveEmails', e.target.checked)}
                    disabled={managedExternally}
                />
            </Property>

            <Property
                title={t('Receive stay related SMS')}
            >
                <Switch
                    id={'stay-receive-sms'}
                    checked={profile.stayReceiveSMS}
                    onChange={(e) => updateField('stayReceiveSMS', e.target.checked)}
                    disabled={managedExternally}
                />
            </Property>

            <Property
                title={t('Receive stay related letters')}
            >
                <Switch
                    id={'stay-receive-letters'}
                    checked={profile.stayReceiveLetters}
                    onChange={(e) => updateField('stayReceiveLetters', e.target.checked)}
                    disabled={managedExternally}
                />
            </Property>

            <Property
                title={t('Letter greeting')}
            >
                <TextField
                    id={'letter-greetings'}
                    size={'small'}
                    value={profile.letterGreetings}
                    onChange={(e) => updateField('letterGreetings', e.target.value)}
                    fullWidth
                    placeholder={managedFieldPlaceholder}
                    disabled={managedExternally}
                />
            </Property>

            <Property
                title={t('Address greeting')}
            >
                <TextField
                    id={'address-greetings'}
                    size={'small'}
                    value={profile.addressGreetings}
                    onChange={(e) => updateField('addressGreetings', e.target.value)}
                    fullWidth
                    placeholder={managedFieldPlaceholder}
                    disabled={managedExternally}
                />
            </Property>

            <Property
                title={t('Opt-in IDs')}
            >
                <Autocomplete
                    id={'opt-in-ids'}
                    size={'small'}
                    multiple
                    freeSolo
                    options={[]}
                    value={profile.optInIds ?? []}
                    fullWidth
                    onChange={(e, value) => updateField('optInIds', value)}
                    renderTags={(value, props) =>
                        value.map((option, index) => (
                            <Chip size={'small'} label={option} {...props({index})} />
                        ))
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            fullWidth
                            size={'small'}
                            placeholder={managedFieldPlaceholder ?? t('Add an opt-in ID and press Enter')}
                            helperText={t('Granular subscription IDs (e.g. Maileon opt-in ids)')}
                        />
                    )}
                    disabled={managedExternally}
                />
            </Property>

            </Stack>
        </AdditionalProperties>
    )

    const phonesSection = (
        <AdditionalProperties title={t('Phones')} variant={'outlined'} defaultExpanded={!!profile.phones?.length}>
            <Stack gap={repeatableItemGap}>
            {
                profile.phones?.map((phone, index) => {
                    const provider = phone.externalInfo?.[0]?.provider
                    const disabled = !!provider
                    return (
                        <Property
                            orientation={'vertical'}
                            key={index}
                            title={phone.isPrimary ? primaryTitle(t('Primary phone')) : t('Phone') + ' ' + (index + 1)}
                            subtitle={provider}
                            sx={{
                                title: {
                                    mt: 2,
                                    fontWeight: 'bold'
                                }
                            }}
                        >
                            <Stack gap={1} width={'100%'}>
                                <PhoneField
                                    phone={phone}
                                    setPhone={phone => {
                                        const phones = [...(profile.phones || [])]
                                        phones[index] = phone
                                        updateField('phones', phones)
                                    }}
                                    index={index}
                                    managedExternally={disabled}
                                />

                                {
                                    !disabled && (
                                        <Stack
                                            direction={'row'}
                                            gap={1}
                                            justifyContent={'flex-end'}
                                            flexWrap={'wrap'}
                                        >
                                            {
                                                !phone.isPrimary && (
                                                    <Button
                                                        variant={'text'}
                                                        size={'small'}
                                                        onClick={() => {
                                                            const phones = [...(profile.phones || [])]
                                                            phones.forEach(e => e.isPrimary = false)
                                                            phones[index] = {
                                                                ...phones[index],
                                                                isPrimary: true
                                                            }
                                                            updateField('phones', phones)
                                                        }}
                                                    >
                                                        {t('Set as primary')}
                                                    </Button>
                                                )
                                            }

                                            <Button
                                                variant={'text'}
                                                size={'small'}
                                                color={'error'}
                                                onClick={() => {
                                                    const phones = [...(profile.phones || [])]
                                                    phones.splice(index, 1)
                                                    updateField('phones', phones)
                                                }}
                                            >
                                                {t('Delete')}
                                            </Button>
                                        </Stack>
                                    )
                                }
                            </Stack>

                        </Property>
                    )
                })
            }

            <Button
                startIcon={<AddIcon/>}
                id={'add-phone'}
                sx={{
                    alignSelf: 'flex-start'
                }}
                onClick={() => {
                    const phones = [...(profile.phones || [])]
                    phones.push({phoneNumberFull: '', isPrimary: false})
                    updateField('phones', phones)
                }}
            >
                {t('Add phone')}
            </Button>
            </Stack>
        </AdditionalProperties>
    )

    const addressesSection = (
        <AdditionalProperties title={t('Addresses')} variant={'outlined'} defaultExpanded={!!profile.addresses?.length}>
            <Stack gap={repeatableItemGap}>
            {
                profile?.addresses?.map((address, index) => {
                    const provider = address.externalInfo?.[0]?.provider
                    const disabled = !!provider
                    return (
                        <Property
                            orientation={'vertical'}
                            key={index}
                            title={address.isPrimary ? primaryTitle(t('Primary address')) : t('Address') + ' ' + (index + 1)}
                            subtitle={provider}
                            sx={{
                                title: {
                                    mt: 2,
                                    fontWeight: 'bold'
                                }
                            }}
                        >
                            <Stack gap={1} width={'100%'}>
                                <AddressField
                                    id={'address'}
                                    value={address}
                                    onChange={(e) => {
                                        const addresses = [...(profile.addresses || [])]
                                        addresses[index] = e!
                                        updateField('addresses', addresses)
                                    }}
                                    index={index}
                                    disabled={disabled}
                                    labelAlignment={'top'}
                                />

                                {
                                    !disabled && (
                                        <Stack
                                            direction={'row'}
                                            gap={1}
                                            justifyContent={'flex-end'}
                                            flexWrap={'wrap'}
                                        >
                                            {
                                                !address.isPrimary && (
                                                    <Button
                                                        variant={'text'}
                                                        size={'small'}
                                                        onClick={() => {
                                                            const addresses = [...(profile.addresses || [])]
                                                            addresses.forEach(e => e.isPrimary = false)
                                                            addresses[index] = {
                                                                ...addresses[index],
                                                                isPrimary: true
                                                            }
                                                            updateField('addresses', addresses)
                                                        }}
                                                    >
                                                        {t('Set as primary')}
                                                    </Button>
                                                )
                                            }

                                            <Button
                                                variant={'text'}
                                                size={'small'}
                                                color={'error'}
                                                onClick={() => {
                                                    const addresses = [...(profile.addresses || [])]
                                                    addresses.splice(index, 1)
                                                    updateField('addresses', addresses)
                                                }}
                                            >
                                                {t('Delete')}
                                            </Button>
                                        </Stack>
                                    )
                                }
                            </Stack>
                        </Property>
                    )
                })
            }

            <Button
                startIcon={<AddIcon/>}
                id={'add-address'}
                sx={{
                    alignSelf: 'flex-start'
                }}
                onClick={() => {
                    const address = [...(profile.addresses || [])]
                    address.push({addressLine1: '', isPrimary: false})
                    updateField('addresses', address)
                }}
            >
                {t('Add address')}
            </Button>
            </Stack>
        </AdditionalProperties>
    )

    const preferencesSection = (
        <AdditionalProperties title={t("Preferences")} variant={'outlined'} defaultExpanded={!!profile.preferences?.length}>
            <Stack gap={repeatableItemGap}>
            {
                profile?.preferences?.map((preference, index) => {
                    const provider = preference.externalInfo?.[0]?.provider
                    const disabled = !!provider
                    return (
                        <Property
                            orientation={'vertical'}
                            key={index}
                            title={t('Preference') + ' ' + (index + 1)}
                            subtitle={provider}
                            sx={{
                                title: {
                                    mt: 2,
                                    fontWeight: 'bold'
                                }
                            }}
                        >
                            <Stack gap={1} width={'100%'}>
                                <PreferencesField
                                    id={'preference'}
                                    index={index}
                                    value={preference}
                                    onChange={(e) => {
                                        const preferences = [...(profile.preferences || [])]
                                        preferences[index] = e!
                                        updateField('preferences', preferences)
                                    }}
                                    disabled={disabled}
                                />

                                {
                                    !disabled && (
                                        <Stack direction={'row'} justifyContent={'flex-end'}>
                                            <Button
                                                variant={'text'}
                                                size={'small'}
                                                color={'error'}
                                                onClick={() => {
                                                    const preferences = [...(profile.preferences || [])]
                                                    preferences.splice(index, 1)
                                                    updateField('preferences', preferences)
                                                }}
                                            >
                                                {t('Delete')}
                                            </Button>
                                        </Stack>
                                    )
                                }
                            </Stack>
                        </Property>
                    )
                })
            }

            <Button
                startIcon={<AddIcon/>}
                id={'add-preference'}
                sx={{
                    alignSelf: 'flex-start'
                }}
                onClick={() => {
                    const preferences = [...(profile.preferences || [])]
                    preferences.push({
                        preferenceType: '',
                        preferenceValue: '',
                        otherPreferenceType: ''
                    })
                    updateField('preferences', preferences)
                }}
            >
                {t('Add preferences')}
            </Button>

            </Stack>
        </AdditionalProperties>
    )

    const notesSection = (
        <AdditionalProperties title={t('Notes')} variant={'outlined'} defaultExpanded={!!profile.internalNotes?.length}>
            <InternalNotes
                data={profileData}
            />
        </AdditionalProperties>
    )

    // Step 3c — PMS Extra Fields section. Read-only display for PMS-sourced entries
    // (Fidelio, Protel, ...), editable for Crqlar-owned entries.
    const extraFieldsSection = (
        <AdditionalProperties title={t('Extra fields')} variant={'outlined'} defaultExpanded={!!profile.extraFields?.length}>
            <ExtraFields data={profileData}/>
        </AdditionalProperties>
    )

    // The profile's own `tags` map. The Akademie's four keys are reserved when the Akademie section
    // above is shown, so one value never has two controls; everywhere else nothing is reserved.
    // Collapsed by default: raw tags are rarely what somebody opens a profile for.
    const dataTagsSection = (
        <AdditionalProperties
            title={t('Data tags')}
            variant={'outlined'}
        >
            {/* No Property wrapper: the section header is already the label, and nesting one
                inside the other printed "Data tags" twice. ExtraFields above does the same. */}
            <Typography variant={'body2'} color={'text.secondary'} sx={{mb: 1}}>
                {t('Custom metadata used by app integrations and guest-app behavior.')}
            </Typography>
            <Form
                formData={editableDataTags(profile.tags, reservedTags)}
                onChange={(e) => updateField('tags', mergeDataTags(profile.tags, e.formData, reservedTags))}
                validator={validator}
                formContext={{submit: false}}
                templates={{ButtonTemplates: {SubmitButton: () => <div/>}}}
                schema={{
                    type: 'object',
                    additionalProperties: [{type: 'string'}],
                    properties: {}
                } as any}
                uiSchema={{}}
            />
        </AdditionalProperties>
    )

    const companySection = (
        <AdditionalProperties title={t('Company')} variant={'outlined'}>
            <CompanyField
                data={profileData}
                managedExternally={managedExternally}
            />
        </AdditionalProperties>
    )

    const membershipSection = (
        <AdditionalProperties title={t("Membership")} variant={'outlined'} defaultExpanded={!!profile.memberships?.length}>
            <Stack gap={repeatableItemGap}>
            {profile?.memberships?.map((membership, index) => {
                const provider = membership.externalInfo?.[0]?.provider
                const disabled = !!provider
                return (
                    <Property
                        orientation={'vertical'}
                        key={index}
                        title={membership.isPrimary ? primaryTitle(t('Primary membership')) : t('Membership') + ' ' + (index + 1)}
                        subtitle={provider}
                        sx={{
                            title: {
                                mt: 2,
                                fontWeight: 'bold'
                            }
                        }}
                    >
                        <Stack gap={1} width={'100%'}>
                            <MembershipField
                                id={'membership'}
                                value={membership}
                                onChange={(e) => {
                                    const memberships = [...(profile.memberships || [])]
                                    memberships[index] = e!
                                    updateField('memberships', memberships)
                                }}
                                index={index}
                                disabled={disabled}
                            />

                            {
                                !disabled && (
                                    <Stack direction={'row'} justifyContent={'flex-end'}>
                                        <Button
                                            variant={'text'}
                                            size={'small'}
                                            color={'error'}
                                            onClick={() => {
                                                const memberships = [...(profile.memberships || [])]
                                                memberships.splice(index, 1)
                                                updateField('memberships', memberships)
                                            }}
                                        >
                                            {t('Delete')}
                                        </Button>
                                    </Stack>
                                )
                            }
                        </Stack>
                    </Property>
                )
            })}

            <Button
                startIcon={<AddIcon/>}
                id={'add-membership'}
                sx={{
                    alignSelf: 'flex-start'
                }}
                onClick={() => {
                    const memberships = [...(profile.memberships || [])]
                    memberships.push({
                        memberName: '',
                        membershipLevel: '',
                        membershipType: '',
                        membershipNumber: '',
                        currentPoints: 0
                    })
                    updateField('memberships', memberships)
                }}
            >
                {t('Add membership')}
            </Button>

            </Stack>
        </AdditionalProperties>
    )

    //endregion UI

    return (
        <Stack
            sx={{
                width: '100%',
                gap: 1,
            }}
        >
            {managedExternallySection}
            {basicInfoSection}
            {isRegularProfile && (
                <Box sx={{mt: {xs: 1.5, sm: 2}}}>
                    {basicInfoSectionExtended}
                </Box>
            )}
            {companySection}
            {contactInfoSection}
            {!readOnly && <Emails data={profileData}/>}
            <Urls data={profileData}/>
            {!readOnly && phonesSection}
            {!readOnly && addressesSection}
            {preferencesSection}
            {membershipSection}
            {notesSection}
            <Documents data={profileData}/>
            {isCompany && <ContactPersons data={profileData}/>}
            {extraFieldsSection}
            {!readOnly && akademie && (
                <AcademySection
                    profile={profile}
                    sector={akademie}
                    onTagsChange={(tags) => updateField('tags', tags)}
                />
            )}
            {!readOnly && dataTagsSection}
            <ExternalInfoPreview externalInfo={profile.externalInfo}/>
        </Stack>
    );
}

export default ProfileDetailsForm;
